import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Paper,
  Chip,
  Divider,
  useTheme,
  FormControlLabel,
  Switch,
  Autocomplete
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import {
  FileDownload,
  Refresh,
  FilterList,
  GetApp
} from '@mui/icons-material';
import { useImportExport } from '../context';
import { ExportSectionProps } from '../types/ComponentTypes';
import { EntityFilter } from '../apiService/importExportApiService';

const ExportSection: React.FC<ExportSectionProps> = ({
  config,
  onExport,
  onError,
  loading = false,
  onOpenDownloadModal
}) => {
  const theme = useTheme();
  const { 
    handleExport, 
    exportLoading, 
    exportError,
    exportData,
    exportFilename,
    availableEntities,
    selectedEntity,
    entityFilters,
    entitiesLoading,
    entitiesError,
    loadEntities,
    selectEntity
  } = useImportExport();
  
  // Export form state
  const [exportFormat, setExportFormat] = useState<'csv' | 'json' | 'zip'>('zip');
  const [exportLimit, setExportLimit] = useState<string>('all');
  const [customLimit, setCustomLimit] = useState<string>('');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  
  // Filter state
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [showFilters, setShowFilters] = useState(false);
  
  // DataGridMAIQ style filter state
  const [filterItems, setFilterItems] = useState<any[]>([]);
  const linkOperator = 'and'; // Always use AND operator
  
  // Date filters
  const [startDate, setStartDate] = useState<DateTime | null>(null);
  const [endDate, setEndDate] = useState<DateTime | null>(null);
  
  // Export completion state
  const [exportCompleted, setExportCompleted] = useState(false);

  // Load entities on component mount
  useEffect(() => {
    loadEntities();
  }, [loadEntities]);

  // Debug export data changes - only log when data actually changes
  useEffect(() => {
    if (exportData && exportData instanceof Blob) {
      // Silent - no console logs
    }
  }, [exportData]);

  // Handle export completion - only run when export is completed and data is available
  useEffect(() => {
    if (exportCompleted && exportData && exportData instanceof Blob) {
      const blobUrl = URL.createObjectURL(exportData);
      
      if (onOpenDownloadModal) {
        // Use filename from server response if available, otherwise generate a default one
        const fileName = exportFilename || `${selectedEntity?.entity || 'export'}_export_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
        const title = `Export ${selectedEntity?.entity ? selectedEntity.entity.charAt(0).toUpperCase() + selectedEntity.entity.slice(1) : 'Data'}`;
        onOpenDownloadModal(blobUrl, title, fileName);
        setExportCompleted(false); // Reset state
      }
    }
  }, [exportCompleted, exportData, exportFilename, selectedEntity?.entity, exportFormat, onOpenDownloadModal]);

  // Get appropriate operator for field type
  const getOperatorForField = (field: string, value: any): string => {
    if (field.includes('date')) {
      if (field.includes('start')) {
        return 'after';
      } else if (field.includes('end')) {
        return 'before';
      }
      return 'is';
    }
    
    if (typeof value === 'boolean') {
      return 'equals'; // For boolean fields, use 'equals' operator
    }
    
    if (typeof value === 'number') {
      return 'equals';
    }
    
    // For text fields, check if it's a select field
    if (entityFilters) {
      const filterConfig = entityFilters.find(f => f.field === field);
      if (filterConfig?.type === 'select') {
        return 'equals';
      }
    }
    
    return 'contains';
  };

  // Initialize filters from selected entity
  useEffect(() => {
    if (entityFilters && entityFilters.length > 0) {
      const initialFilters: Record<string, any> = {};
      const initialFilterItems: any[] = [];
      
      entityFilters.forEach(filter => {
        // Use defaultValue from API if available, otherwise use empty string
        const defaultValue = filter.defaultValue !== undefined ? filter.defaultValue : '';
        initialFilters[filter.field] = defaultValue;
        
        // Add to DataGridMAIQ filter items if there's a default value
        if (defaultValue !== '' && defaultValue !== null && defaultValue !== undefined) {
          const operatorValue = getOperatorForField(filter.field, defaultValue);
          initialFilterItems.push({
            columnField: filter.field,
            operatorValue: operatorValue,
            value: defaultValue
          });
        }
      });
      
      setFilters(initialFilters);
      setFilterItems(initialFilterItems);
    }
  }, [entityFilters]);

  const handleFilterChange = (field: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Update DataGridMAIQ style filter items
    updateFilterItems(field, value);
  };

  // Update filter items in DataGridMAIQ format
  const updateFilterItems = (field: string, value: any) => {
    setFilterItems(prev => {
      // Remove existing filter for this field
      const filtered = prev.filter(item => item.columnField !== field);
      
      // For boolean fields, always add filter (true/false)
      // For date fields, add if value exists and is not null and not empty string
      // For other fields, only add if value exists and is not empty
      const shouldAddFilter = typeof value === 'boolean' || 
                             (field.includes('date') && value && value !== null && value !== '') ||
                             (!field.includes('date') && value && value !== '' && value !== null);
      
      if (shouldAddFilter) {
        const operatorValue = getOperatorForField(field, value);
        const newFilter = {
          columnField: field,
          operatorValue: operatorValue,
          value: value
        };
        const updatedItems = [...filtered, newFilter];
        return updatedItems;
      }
      
      return filtered;
    });
  };

  // Helper function to safely convert DateTime to ISO string
  const safeToISOString = (date: DateTime | null): string | null => {
    if (!date) {
      return null;
    }
    
    // Check if it's a Luxon DateTime object
    if (typeof date === 'object' && 'isValid' in date) {
      if (!date.isValid) {
        return null;
      }
      try {
        // Use toISO() method for Luxon DateTime and convert to UTC to remove timezone offset
        const isoString = date.toUTC().toISO();
        return isoString;
      } catch (error) {
        return null;
      }
    }
    
    // If it's already a string, return as is
    if (typeof date === 'string') {
      return date;
    }
    
    return null;
  };

  const handleExportClick = async () => {
    if (!selectedEntity) {
      onError(new Error('Please select an entity type first'));
      return;
    }

    try {
      setExportCompleted(false);
      // Create DataGridMAIQ style filter model
      const filterModel = {
        items: filterItems,
        linkOperator: linkOperator
      };
      
      // Create export parameters
      const exportParams: Record<string, any> = {
        format: exportFormat,
        filters: filterModel, // Pass filters as DataGridMAIQ style object
        entityType: selectedEntity.entity // Use selected entity instead of config
      };
      
      // Add individual date filters for easier API handling
      if (startDate && startDate.isValid) {
        exportParams.start_date = safeToISOString(startDate);
      }
      
      if (endDate && endDate.isValid) {
        exportParams.end_date = safeToISOString(endDate);
      }
      
      // Add limit if specified
      if (exportLimit !== 'all') {
        exportParams.limit = exportLimit === 'custom' ? customLimit : exportLimit;
      }
      
      // Add selected fields
      if (selectedFields.length > 0) {
        exportParams.fields = selectedFields;
      }
      
      // Add dealer ID if available
      if (config.dealerId) {
        exportParams.dealer = config.dealerId;
      }
      
      // Call export API first to get the blob data
      await handleExport(exportParams);
      
      // Mark export as completed - the useEffect will handle the rest
      setExportCompleted(true);
      
    } catch (error) {
      onError(error);
    }
  };

  const handleResetFilters = () => {
    setFilters({});
    setFilterItems([]);
    setStartDate(null);
    setEndDate(null);
    setExportLimit('all');
    setCustomLimit('');
    setSelectedFields([]);
    
    // Reset filters based on selected entity with default values
    if (entityFilters && entityFilters.length > 0) {
      const initialFilters: Record<string, any> = {};
      const initialFilterItems: any[] = [];
      
      entityFilters.forEach(filter => {
        const defaultValue = filter.defaultValue !== undefined ? filter.defaultValue : '';
        initialFilters[filter.field] = defaultValue;
        
        // Add to DataGridMAIQ filter items if there's a default value
        if (defaultValue !== '' && defaultValue !== null && defaultValue !== undefined) {
          const operatorValue = getOperatorForField(filter.field, defaultValue);
          initialFilterItems.push({
            columnField: filter.field,
            operatorValue: operatorValue,
            value: defaultValue
          });
        }
      });
      
      setFilters(initialFilters);
      setFilterItems(initialFilterItems);
    }
  };

  const renderFilterField = (filter: EntityFilter) => {
    const value = filters[filter.field] !== undefined ? filters[filter.field] : (filter.defaultValue !== undefined ? filter.defaultValue : '');
    
    switch (filter.type) {
      case 'date':
        return (
          <DatePicker
            label={filter.label}
            value={filter.field.includes('start') ? startDate : endDate}
            onChange={(newValue) => {
              try {
                if (filter.field.includes('start')) {
                  setStartDate(newValue);
                } else {
                  setEndDate(newValue);
                }
                
                // Safely handle date conversion with proper null checks
                const dateValue = safeToISOString(newValue);
                
                // Always call handleFilterChange - let it handle null/empty values
                handleFilterChange(filter.field, dateValue);
                
              } catch (error) {
                // Reset the date if there's an error
                if (filter.field.includes('start')) {
                  setStartDate(null);
                } else {
                  setEndDate(null);
                }
                // Call handleFilterChange with null to remove the filter
                handleFilterChange(filter.field, null);
              }
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                placeholder: filter.placeholder,
                size: 'small',
                error: false // Ensure no error state from parent
              }
            }}
            // Add additional props for better error handling
            disableFuture={false}
            disablePast={false}
            format="MM/dd/yyyy"
          />
        );
        
      case 'select':
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{filter.label}</InputLabel>
            <Select
              value={value}
              label={filter.label}
              onChange={(e) => handleFilterChange(filter.field, e.target.value)}
            >
              <MenuItem value="">
                <em>Select {filter.label}</em>
              </MenuItem>
              {/* For now, we'll add common options based on field name */}
              {filter.field.includes('status') && (
                <>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </>
              )}
              {filter.field.includes('type') && (
                <>
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="premium">Premium</MenuItem>
                </>
              )}
            </Select>
          </FormControl>
        );
        
      case 'boolean':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {filter.label}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(value)}
                  onChange={(e) => handleFilterChange(filter.field, e.target.checked)}
                  color="primary"
                />
              }
              label={Boolean(value) ? 'Yes' : 'No'}
              labelPlacement="start"
            />
          </Box>
        );
        
      case 'number':
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            label={filter.label}
            value={value}
            onChange={(e) => handleFilterChange(filter.field, e.target.value)}
            placeholder={filter.placeholder}
          />
        );
        
      default:
        return (
          <TextField
            fullWidth
            size="small"
            label={filter.label}
            value={value}
            onChange={(e) => handleFilterChange(filter.field, e.target.value)}
            placeholder={filter.placeholder}
          />
        );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Box sx={{ p: 2.5 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            color: '#0F2A52',
            fontWeight: 600,
            fontSize: '1.1rem',
            mb: 1
          }}
        >
          Export Data
        </Typography>
        
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 3,
            fontSize: '0.85rem',
            lineHeight: 1.5
          }}
        >
          Select an entity type and export your data in various formats with dynamic filters.
        </Typography>

        {exportError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {exportError}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Entity Selection */}
          <Grid item xs={12}>
            <Card 
              elevation={0}
              sx={{
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                border: '1px solid rgba(15, 40, 76, 0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    color: '#0F2A52',
                    fontWeight: 600,
                    fontSize: '1rem',
                    mb: 2
                  }}
                >
                  Select Entity Type
                </Typography>
                
                {entitiesError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {entitiesError}
                  </Alert>
                )}
                
                <FormControl fullWidth size="small">
                  <InputLabel>Choose Entity</InputLabel>
                  <Select
                    value={selectedEntity?.entity || ''}
                    label="Choose Entity"
                    onChange={(e) => {
                      const entity = availableEntities.find(ent => ent.entity === e.target.value);
                      if (entity) {
                        selectEntity(entity);
                      }
                    }}
                    disabled={entitiesLoading}
                  >
                    {entitiesLoading ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        Loading entities...
                      </MenuItem>
                    ) : (
                      availableEntities.map((entity) => (
                        <MenuItem key={entity.entity} value={entity.entity}>
                          {entity.entity.charAt(0).toUpperCase() + entity.entity.slice(1)}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                
                {selectedEntity && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Selected: {selectedEntity.entity} 
                    {entityFilters.length > 0 && ` (${entityFilters.length} filters available)`}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Export Configuration */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={0}
              sx={{
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                border: '1px solid rgba(15, 40, 76, 0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography 
                  variant="h6" 
                  gutterBottom
                  sx={{
                    color: '#0F2A52',
                    fontWeight: 600,
                    fontSize: '1rem',
                    mb: 2
                  }}
                >
                  Export Configuration
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Export Format</InputLabel>
                      <Select
                        value={exportFormat}
                        label="Export Format"
                        onChange={(e) => setExportFormat(e.target.value as any)}
                      >
                        {config.supportedFormats.map((format) => (
                          <MenuItem key={format} value={format}>
                            {format.toUpperCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Record Limit</InputLabel>
                      <Select
                        value={exportLimit}
                        label="Record Limit"
                        onChange={(e) => setExportLimit(e.target.value)}
                      >
                        <MenuItem value="all">All Records</MenuItem>
                        <MenuItem value="100">100 Records</MenuItem>
                        <MenuItem value="500">500 Records</MenuItem>
                        <MenuItem value="1000">1,000 Records</MenuItem>
                        <MenuItem value="5000">5,000 Records</MenuItem>
                        <MenuItem value="custom">Custom Limit</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {exportLimit === 'custom' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Custom Limit"
                        value={customLimit}
                        onChange={(e) => setCustomLimit(e.target.value)}
                        placeholder="Enter number of records"
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Filters */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={0}
              sx={{
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                border: '1px solid rgba(15, 40, 76, 0.08)',
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography 
                    variant="h6"
                    sx={{
                      color: '#0F2A52',
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}
                  >
                    Filters
                  </Typography>
                  <Button
                    startIcon={<FilterList />}
                    onClick={() => setShowFilters(!showFilters)}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: '#0F2A52',
                      color: '#0F2A52',
                      '&:hover': {
                        borderColor: '#0A1F3F',
                        backgroundColor: 'rgba(15, 42, 82, 0.04)',
                      },
                    }}
                  >
                    {showFilters ? 'Hide' : 'Show'} Filters
                  </Button>
                </Box>
                
                {showFilters && entityFilters && entityFilters.length > 0 && (
                  <Grid container spacing={2}>
                    {entityFilters.map((filter) => (
                      <Grid item xs={12} key={filter.field}>
                        {renderFilterField(filter)}
                      </Grid>
                    ))}
                    
                    <Grid item xs={12}>
                      <Button
                        onClick={handleResetFilters}
                        size="small"
                        variant="outlined"
                        startIcon={<Refresh />}
                        sx={{
                          borderColor: '#0F2A52',
                          color: '#0F2A52',
                          '&:hover': {
                            borderColor: '#0A1F3F',
                            backgroundColor: 'rgba(15, 42, 82, 0.04)',
                          },
                        }}
                      >
                        Reset Filters
                      </Button>
                    </Grid>
                  </Grid>
                )}
                
                {(!showFilters || !entityFilters || entityFilters.length === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedEntity ? 'No filters available for this entity type.' : 'Please select an entity type to see available filters.'}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Field Selection */}
          {config.exportFields && config.exportFields.length > 0 && (
            <Grid item xs={12}>
              <Card 
                elevation={0}
                sx={{
                  borderRadius: 1.5,
                  boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                  border: '1px solid rgba(15, 40, 76, 0.08)',
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      color: '#0F2A52',
                      fontWeight: 600,
                      fontSize: '1rem',
                      mb: 2
                    }}
                  >
                    Field Selection
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select which fields to include in the export. Leave empty to include all fields.
                  </Typography>
                  
                  <Autocomplete
                    multiple
                    options={config.exportFields}
                    value={selectedFields}
                    onChange={(event, newValue) => setSelectedFields(newValue)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={option}
                          {...getTagProps({ index })}
                          key={option}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Select fields to export"
                      />
                    )}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Export Button */}
          <Grid item xs={12}>
            <Paper 
              sx={{ 
                p: 2.5, 
                backgroundColor: 'background.paper',
                borderRadius: 1.5,
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
                border: '1px solid rgba(15, 40, 76, 0.08)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{
                      color: '#0F2A52',
                      fontWeight: 600,
                      fontSize: '1rem',
                      mb: 1
                    }}
                  >
                    Ready to Export
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                      fontSize: '0.85rem',
                      lineHeight: 1.5
                    }}
                  >
                    Click the export button to download your data with the selected configuration.
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  size="large"
                  startIcon={exportLoading ? <CircularProgress size={20} /> : <FileDownload />}
                  onClick={handleExportClick}
                  disabled={exportLoading || loading || !selectedEntity}
                  sx={{ 
                    minWidth: 150,
                    backgroundColor: '#0F2A52',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#0A1F3F',
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(15, 42, 82, 0.3)',
                      color: 'rgba(255, 255, 255, 0.5)',
                    },
                  }}
                >
                  {exportLoading ? 'Exporting...' : selectedEntity ? 'Export Data' : 'Select Entity First'}
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default ExportSection;
